import { router } from '@/router'
import { deviceType, isTouch, orientation } from '@/utils/device'
import { getEffectiveCadenceInWeeks } from '@/utils/plan'
import {
  accountPlanType,
  pickPrimarySubscription,
  readAddress,
  readDogs,
  readIsAuthenticated,
  readProfile,
  readSubscriptions,
} from '@/analytics/properties'
import { SubscriptionStatus } from '@lyka-pet-food/contracts'
import type { SuperProperties, PeopleProfileProperties } from '@/types/analytics'

const REACTIVATION_ELIGIBLE_STATUSES: string[] = [SubscriptionStatus.paused, SubscriptionStatus.cancelled]

const locationFromRoute = (): SuperProperties['location'] =>
  router.currentRoute.value.meta.analyticsLocation ?? null

/**
 * Super properties attached to every analytics event (Segment + CIO + Mixpanel).
 * Computed at event time from the vue-query cache, auth store, router meta, and window so they
 * are always current without watchers or setup-context coupling. Best-effort: every read is
 * guarded (see properties.ts / device.ts) so analytics never breaks an app flow.
 */
export const getSuperProperties = (): SuperProperties => {
  const profile = readProfile()
  const subscription = pickPrimarySubscription(readSubscriptions())
  const activeDogs = readDogs().filter((d) => !d.deactivatedAt)
  const status = subscription?.status ?? null

  return {
    is_phoenix: true,
    brand: 'lyka',
    app_surface: 'customer_account',
    platform: 'web',
    location: locationFromRoute(),
    device_type: deviceType(),
    viewport_width: window.innerWidth || null,
    viewport_height: window.innerHeight || null,
    orientation: orientation(),
    is_touch: isTouch(),
    is_authenticated: readIsAuthenticated(),
    tenant: profile?.tenantType ?? null,
    subscription_status: status,
    is_reactivation_eligible: status != null && REACTIVATION_ELIGIBLE_STATUSES.includes(status),
    account_plan_type: accountPlanType(subscription),
    delivery_frequency: subscription ? getEffectiveCadenceInWeeks(subscription) : null,
    dog_count: activeDogs.length,
    subscription_id: subscription?.id ?? null,
  }
}

/** People profile properties (Mixpanel People API) — PII + account context. */
export const getPeopleProfileProperties = (): PeopleProfileProperties => {
  const profile = readProfile()
  const subscription = pickPrimarySubscription(readSubscriptions())
  const activeDogs = readDogs().filter((d) => !d.deactivatedAt)
  const address = readAddress()

  return {
    email: profile?.email ?? null,
    first_name: profile?.firstName ?? null,
    last_name: profile?.lastName ?? null,
    phone: profile?.phoneNumber ?? null,
    tenant: profile?.tenantType ?? null,
    cognito_user_id: profile?.cognitoUserId ?? null,
    brand: 'lyka',
    subscription_status: subscription?.status ?? null,
    account_plan_type: accountPlanType(subscription),
    delivery_frequency: subscription ? getEffectiveCadenceInWeeks(subscription) : null,
    dog_count: activeDogs.length,
    state: address?.state ?? null,
  }
}
